// ── Shared value formatting ──────────────────────────────────────────────
// Ported from instance-panel.jsx `lesHelpers.formatValue`. Returns a styled
// node for a record's field value, by field type. Exposed both as a component
// (<FormatValue ... />) for templates and as `formatValue(...)` returning a
// VNode for the few render-function call sites — so list/card/grid/table and
// the instance panel format values identically.

import { h } from 'vue'

import { valuesAt } from '@/admin-collection-layout/composables/fqlSearchHelpers.js'

const mono = 'var(--p-font-mono, ui-monospace, monospace)'

// One of each, built once: a formatter is costly to construct and a table
// formats every cell. `undefined` takes the reader's own locale.
const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })
const numberFormat = new Intl.NumberFormat()

// A dotted path is read through the record, and a path through an array reads
// every entry: `location.city` is the city, `claims.claimType` every claim type.
function renderValue(record, key, fieldDefinitions, fieldMeta) {
  const decl = ((fieldDefinitions && fieldDefinitions.fields) || {})[key] || {}
  const meta = (fieldMeta || {})[key] || {}
  const values = valuesAt(record, key).filter((v) => v != null && v !== '')
  if (values.length === 0) return h('span', { style: { color: 'var(--p-surface-400)' } }, '—')
  if (values.length === 1) return renderOne(values[0], decl, meta)
  return h('span', values.flatMap((v, i) => (i === 0 ? [renderOne(v, decl, meta)] : [', ', renderOne(v, decl, meta)])))
}

function renderOne(raw, decl, meta) {
  if (decl.type === 'enum') {
    const em = (meta.enumMeta && meta.enumMeta[raw]) || null
    return h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: '6px' } }, [
      em && em.color
        ? h('span', { style: { width: '8px', height: '8px', borderRadius: '999px', background: em.color, flexShrink: 0 } })
        : null,
      (em && em.label) || raw,
    ])
  }
  if (decl.type === 'boolean') {
    const on = raw === true || raw === 'true'
    return h(
      'span',
      { style: { display: 'inline-flex', alignItems: 'center', gap: '5px', color: on ? 'var(--p-primary-color)' : 'var(--p-text-muted-color)' } },
      [h('i', { class: `pi pi-${on ? 'check-circle' : 'times-circle'}`, style: { fontSize: '12px' } }), on ? 'Yes' : 'No'],
    )
  }
  if (decl.type === 'objectId') return h('span', { style: { fontFamily: mono, fontSize: '.92em' } }, raw)
  // The exact stored instant stays one hover away, for somebody matching a log.
  if (decl.type === 'date' || meta.date) {
    const when = new Date(raw)
    if (Number.isNaN(when.getTime())) return h('span', String(raw))
    return h('span', { title: String(raw), style: { fontVariantNumeric: 'tabular-nums' } }, dateFormat.format(when))
  }
  if (decl.type === 'number') return h('span', { style: { fontVariantNumeric: 'tabular-nums' } }, numberFormat.format(Number(raw)))
  return h('span', String(raw))
}

export const FormatValue = {
  name: 'FormatValue',
  props: {
    record: { type: Object, required: true },
    fieldKey: { type: String, required: true },
    fieldDefinitions: { type: Object, required: true },
    fieldMeta: { type: Object, default: () => ({}) },
  },
  setup(props) {
    return () => renderValue(props.record, props.fieldKey, props.fieldDefinitions, props.fieldMeta)
  },
}

export function formatValue(record, key, fieldDefinitions, fieldMeta) {
  return h(FormatValue, { record, fieldKey: key, fieldDefinitions, fieldMeta })
}

export default FormatValue
